import React, { useMemo, useRef } from "react";
import Plot from "../../graphViewV2/charts/PlotlyChart";
import {
  plotlyAxisBase,
  plotlyAxisTitle,
  plotlyAxisBaseCategoricalX,
} from "../../graphViewV2/charts/plotlyAxisTheme";
import { buildPopulationCumulativeDualAxisTicks } from "../../graphViewV2/charts/axisTicks";
import {
  GraphV2FullscreenInlineRow,
  POND_STAT_DESKTOP_HEIGHT,
  POND_STAT_MOBILE_HEIGHT,
  useGraphV2PlotHeight,
} from "../../graphViewV2/ChartFullscreenShell";
import { LABEL } from "../../../../utils/samplingDisplayLabels";

/**
 * @param {object} props
 * @param {boolean} props.isMobile
 * @param {string} props.binUnitLabel - e.g. "Inches" or "mm"
 * @param {{ binLabels: string[], percents: number[], cumulative: number[] }} props.data
 */
export default function PondStatPopulationDualAxisChart({ isMobile, binUnitLabel, data }) {
  const plotRef = useRef(null);
  const { binLabels, percents, cumulative } = data || { binLabels: [], percents: [], cumulative: [] };

  const dualTicks = useMemo(() => {
    const m = percents.reduce((a, p) => (Number.isFinite(p) && p > a ? p : a), 0);
    return buildPopulationCumulativeDualAxisTicks(m);
  }, [percents]);

  const plotHeight = useGraphV2PlotHeight({
    isMobile,
    mobileDefault: POND_STAT_MOBILE_HEIGHT,
    desktopDefault: POND_STAT_DESKTOP_HEIGHT,
    legendExtra: 0,
  });

  if (!binLabels.length) {
    return (
      <p className="text-muted text-center py-6 mb-0">No length-bin data for this session.</p>
    );
  }

  const traces = [
    {
      type: "bar",
      name: LABEL.PCT_OF_POPULATION,
      x: binLabels,
      y: percents,
      marker: { color: "#1e3a5f" },
    },
    {
      type: "scatter",
      mode: "lines+markers",
      name: LABEL.CUM_PCT_OF_POPULATION,
      x: binLabels,
      y: cumulative,
      yaxis: "y2",
      line: { color: "#ea580c", width: 3 },
      marker: { color: "#ea580c", size: 8 },
    },
  ];

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <GraphV2FullscreenInlineRow />
      <Plot
          ref={plotRef}
          data={traces}
          layout={{
            autosize: true,
            height: plotHeight,
            margin: { t: 56, r: 72, b: 72, l: 64 },
            paper_bgcolor: "white",
            plot_bgcolor: "white",
            barmode: "group",
            xaxis: {
              title: plotlyAxisTitle(`Bin size (${binUnitLabel})`, isMobile),
              ...plotlyAxisBaseCategoricalX(isMobile, "default"),
            },
            yaxis: {
              title: plotlyAxisTitle(LABEL.PCT_OF_POPULATION, isMobile),
              tickmode: "array",
              tickvals: dualTicks.leftTicks,
              range: [0, dualTicks.leftMax],
              ...plotlyAxisBase(isMobile, "default"),
            },
            yaxis2: {
              title: plotlyAxisTitle(LABEL.CUM_PCT_OF_POPULATION, isMobile),
              overlaying: "y",
              side: "right",
              tickmode: "array",
              tickvals: dualTicks.rightTicks,
              range: [0, dualTicks.rightMax],
              ...plotlyAxisBase(isMobile, "default"),
              showgrid: false,
              zeroline: false,
            },
            legend: { orientation: "h", yanchor: "bottom", y: 1.02, x: 0 },
            hovermode: "x unified",
          }}
          config={{ responsive: true, displaylogo: false }}
          style={{ width: "100%" }}
          useResizeHandler
        />
    </div>
  );
}
